import * as angular from 'angular';

import extendDeep from './extendDeep';

export default function BenchmarkGraphService(BenchmarkGraphConfig) {
  'ngInject';

  // This is our bell curve equation, we call it for each value x from 0 - 100
  // and it returns the appropriate y position, generating the bell curve
  var calcY = function (x) {
    return (Math.sin(2 * Math.PI * (x / 100 - 1 / 4)) + 1) / 2;
  };

  var first = [];
  var second = [];

  for (let i = 0; i < 51; i++) {
    first.push([i, calcY(i)]);
  }

  for (let i = 50; i < 101; i++) {
    second.push([i, calcY(i)]);
  }

  var series = [
    {
      id: 'up-slope',
      color: '#E36F1E',
      type: 'spline',
      data: first,
      showInLegend: false
    },
    {
      id: 'down-slope',
      color: '#8DC63F',
      type: 'spline',
      data: second,
      showInLegend: false
    }
  ];

  var config = BenchmarkGraphConfig.getDefaultBellCurveGraphConfig(series);

  var sizeConfig = {};

  return {
    verticalOffset: 0.005,
    getAdjustedY: function (x) {
      // Y should never be less than 0
      var y = calcY(x);

      var v = this.verticalOffset;

      /* tslint:disable:curly */
      if (x > 45 && x < 55) v *= 0.75;
      else if (x > 40 && x < 60) v *= 0.85;
      else if (x > 35 && x < 65) v *= 0.9;
      else if (x < 15 || x > 85) v *= 0.9;
      else if (x < 10 || x > 90) v *= 0.8;
      else if (x < 5 || x > 95) v *= 0.5;
      /* tslint:enable:curly */

      if (y < v) {
        y = 0;
      } else {
        y -= v;
      }

      return y;
    },
    getAdjustedYForMarker: calcY, // No whitespace adjustment for now
    /**
     * Call setSize first since this depends on those values
     * TODO: This should only set the current / planned spend values
     * TODO: getConfig should create the actual config obj
     * Rule: negative savings percentages and amounts should always be orange
     */
    setRankings: function (current, planned, savingsAmount, size) {
      var fill = [];

      var currentColor = '#929396';
      var plannedColor = '#000';
      var savingsLineColor = '#8DC63F'; // positive savings
      var losingLineColor = '#E36F1E'; // negative savings
      var noSavingsLineColor = '#CECECF'; // $0 savings

      var markers = [];

      // Show Current and Planned Spend
      // console.log('showing markers: current marker: ' + current + ', planned marker: ' + planned);
      if (planned != null) {
        current = 100 - current;
        planned = 100 - planned;

        // We always start with the low number and go to the high one
        // We just change the color of the line to reflect positive or negative savings
        var start = planned > current ? current : planned;
        var end = planned > current ? planned : current;

        var fillColor;
        if (savingsAmount != null) {
          if (savingsAmount > 0) {
            fillColor = savingsLineColor;
          } else if (savingsAmount < 0) {
            fillColor = losingLineColor;
          } else {
            fillColor = noSavingsLineColor;
          }
        } else {
          // console.log('warning: savings not specified, using marker difference');
          fillColor = planned > current ? savingsLineColor : losingLineColor;
        }
        var startColor = planned > current ? currentColor : plannedColor;
        var endColor = planned > current ? plannedColor : currentColor;

        config.plotOptions.areaspline.fillColor = fillColor;

        if (planned != current) {
          for (var i = start; i <= end; i++) {
            fill.push({
              x: i,
              y: this.getAdjustedY(i),
              marker: {
                enabled: false
              }
            });
          }
        } else {
          // When the planned spend marker and current spend marker are the same, we want to show a 1px vertical line
          // indicating positive savings, negative savings, or no savings based on savingsAmount
          var range = [];

          if (savingsAmount > 0) {
            range = [start, start + 1];
          } else if (savingsAmount < 0) {
            range = [start - 1, start];
          } else {
            // this looks best when $0 savings
            range = [start - 1, start];
          }

          var self = this;
          angular.forEach(range, function (x) {
            fill.push({
              x: x,
              y: self.getAdjustedY(x),
              marker: {
                enabled: false
              }
            });
          });
        }

        // If markers are different, show them both
        if (planned != current) {
          markers.push({
            x: start,
            y: this.getAdjustedYForMarker(start),
            id: 'currentSpendPoint',
            marker: {
              enabled: true,
              fillColor: startColor,
              states: {
                hover: {
                  enabled: true
                }
              }
            }
          });

          markers.push({
            x: end,
            y: this.getAdjustedYForMarker(end),
            id: 'plannedSpendPoint',
            marker: {
              enabled: true,
              fillColor: endColor,
              states: {
                hover: {
                  enabled: true
                }
              }
            }
          });
        } else {
          // Otherwise just show one marker with a split image
          var useSmallImage =
            size == 'small' || size == 'mini' || size == 'impact';
          markers.push({
            x: end,
            y: this.getAdjustedYForMarker(end),
            id: 'plannedSpendPoint',
            marker: {
              enabled: true,
              fillColor: endColor,
              symbol:
                'url(/aptitude/styles/images/purchase-planning/bellcurve-half' +
                (useSmallImage ? '12' : '20') +
                '.png)',
              states: {
                hover: {
                  enabled: true
                }
              }
            }
          });
        }
      } else {
        current = 100 - current;

        // Only show Current Spend
        fill.push({
          x: current,
          y: this.getAdjustedY(current),
          marker: {
            enabled: true,
            fillColor: currentColor,
            states: {
              hover: {
                enabled: true
              }
            }
          }
        });
      }

      // The first two are the red / green slope lines
      // Remove all the rest before we add the new ones
      while (config.series.length > 2) {
        config.series.pop();
      }

      config.series.push({
        id: 'savings-range',
        type: 'areaspline',
        color: fillColor,
        data: fill,
        showInLegend: false
      });

      if (markers.length) {
        config.series.push({
          id: 'savings-markers',
          type: 'scatter',
          color: 'none',
          data: markers,
          showInLegend: false
        });
      }

      //SVG uses 'painters model' when adding path elements
      //meaning last path element added will be the one on top
      //so to not break highcharts rule of ascending order for data
      //in a series we will use the load event to arrange overlapping points
      //by grabbing the point by Id, removing it, then adding it back so it's redrawn on top
      //ideally we may want to add the planned spend dot in the load event - LG
      config.chart.events = {
        load: function (event) {
          var marker = this.get('plannedSpendPoint');

          if (marker) {
            var options = marker.options;
            var series = marker.series;

            marker.remove();
            series.addPoint(options);
          }
        }
      };

      return this;
    },
    setSizeConfig: function (sizeConfigOverrides) {
      sizeConfig = sizeConfigOverrides;
    },
    setSize: function (size) {
      switch (size) {
        case 'mini':
          sizeConfig = BenchmarkGraphConfig.getMiniOverrides();
          break;
        case 'small':
          sizeConfig = BenchmarkGraphConfig.getSmallOverrides();
          break;
        case 'impact':
          sizeConfig = BenchmarkGraphConfig.getImpactOverrides();
          break;
        case 'medium':
          sizeConfig = BenchmarkGraphConfig.getMediumOverrides();
          break;
        case 'modal':
          sizeConfig = BenchmarkGraphConfig.getModalOverrides();

          this.verticalOffset = 0.03;
          break;
        case 'large':
          sizeConfig = BenchmarkGraphConfig.getLargeOverrides();
          break;
        default:
          //console.log('Warning: size not detected, returning default');

          sizeConfig = BenchmarkGraphConfig.getDefaultOverrides();
          break;
      }

      return this;
    },
    setGraphClickHandler: function (fn) {
      this.graphClickHandler = fn;
    },
    getHighchartConfig: function () {
      // console.groupCollapsed('getHighchartConfig():');
      //   console.log('verticalOffset');
      //   console.log(this.verticalOffset);

      //   console.log('config:');
      //   console.log(config);

      //   console.log('sizeConfig:');
      //   console.log(sizeConfig);

      // TODO: Fix config from being overriden each time this runs
      var mergedConfig = extendDeep(config, sizeConfig);

      //   console.log('merged config + sizeConfig');
      //   console.log(mergedConfig);
      // console.groupEnd();

      // This fixes the double click issue, but is there still an underlying inheritance issue?
      if (this.graphClickHandler == null) {
        this.graphClickHandler = function () {
          //
        }; // noop
      }

      if (this.graphClickHandler) {
        extendDeep(mergedConfig, {
          chart: {
            events: {
              click: this.graphClickHandler
            }
          },
          plotOptions: {
            series: {
              events: {
                click: this.graphClickHandler
              }
            },
            areaspline: {
              events: {
                click: this.graphClickHandler
              }
            }
          }
        });
      }

      // console.log('bell graph config:');
      // console.log(mergedConfig);

      return mergedConfig;
    }
  };
}
